/**
 * VehicleAutocomplete Component
 *
 * Live autocomplete for VIN and license plate fields that searches
 * unassigned vehicles from the bulk import. When a vehicle is selected,
 * it gets assigned to the current customer.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Box,
  Paper,
  Typography,
  CircularProgress,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import { useDebounce } from '../../../../hooks/useDebounce';
import { apiClient } from '../../../../services/api.service';
import { logger } from '../../../../utils/logger';

interface UnassignedVehicle {
  id: string;
  truckName: string | null;
  licensePlate: string | null;
  vin: string | null;
  registrationDate: string | null;
  biAnnualDate: string | null;
  serviceCount: number;
}

interface VehicleAutocompleteProps {
  /** Current field value */
  value: string;
  /** Which field this autocomplete is for */
  field: 'vin' | 'licensePlate';
  /** Called when user types (updates local field value) */
  onChange: (value: string) => void;
  /** Called when user selects an unassigned vehicle from the dropdown */
  onVehicleSelected: (vehicle: UnassignedVehicle) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the field is disabled (e.g. during OCR) */
  disabled?: boolean;
  /** Custom sx for the TextField */
  sx?: object;
}

export const VehicleAutocomplete: React.FC<VehicleAutocompleteProps> = ({
  value,
  field,
  onChange,
  onVehicleSelected,
  placeholder,
  disabled = false,
  sx,
}) => {
  const [results, setResults] = useState<UnassignedVehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebounce(value, 300);

  // Search unassigned vehicles when query changes
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    const search = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get<{ results: UnassignedVehicle[]; total: number }>(
          '/api/v1/vehicles/unassigned/search',
          { params: { q: debouncedQuery, limit: 8 } }
        );
        const data = response.data;
        // Filter out results where the matching field is empty
        const filtered = data.results.filter((v: UnassignedVehicle) => {
          if (field === 'vin') return !!v.vin;
          return !!v.licensePlate;
        });
        setResults(filtered);
        setOpen(filtered.length > 0);
      } catch (error) {
        logger.error('Unassigned vehicle search failed', { error });
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    };

    search();
  }, [debouncedQuery, field]);

  const handleSelect = (vehicle: UnassignedVehicle) => {
    setOpen(false);
    setResults([]);
    onVehicleSelected(vehicle);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ position: 'relative', width: '100%' }}>
        <TextField
          value={value}
          onChange={handleInputChange}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
          variant="outlined"
          size="small"
          fullWidth
          placeholder={placeholder}
          disabled={disabled}
          InputProps={{
            endAdornment: loading ? (
              <CircularProgress size={16} sx={{ mr: 0.5 }} />
            ) : null,
          }}
          sx={sx}
        />
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 9999, width: anchorRef.current?.offsetWidth }}
        >
          <Paper
            elevation={8}
            sx={{
              maxHeight: 280,
              overflow: 'auto',
              mt: 0.5,
              border: '1px solid #e5e7eb',
            }}
          >
            {results.map((vehicle) => (
              <Box
                key={vehicle.id}
                onClick={() => handleSelect(vehicle)}
                sx={{
                  px: 1.5,
                  py: 1,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#f3f4f6' },
                  borderBottom: '1px solid #f3f4f6',
                }}
              >
                {/* Primary: the field being searched */}
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                  {field === 'vin' ? vehicle.vin : vehicle.licensePlate}
                </Typography>
                {/* Secondary: the other field + truck name */}
                <Typography variant="caption" color="text.secondary">
                  {field === 'vin'
                    ? `Plate: ${vehicle.licensePlate || 'N/A'}`
                    : `VIN: ${vehicle.vin || 'N/A'}`}
                  {vehicle.truckName ? ` · ${vehicle.truckName}` : ''}
                  {vehicle.serviceCount > 0 ? ` · ${vehicle.serviceCount} services` : ''}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};
